import type { IndoorRoomConfig } from "../domain/indoor/indoorRoom";
/**
 * Zustand store for the builder's design state.
 *
 * Replaces the old useBuilderDesignState hook with a single store
 * that manages draft state (dimensions, openings, thermal, visibility, etc.)
 * and persistence (auto-save, design loading).
 *
 * Domain logic (kapnota, materials, BBR compliance) is NOT stored here —
 * it is derived via useMemo in BuilderPage from buildInput → engine.calculateMaterials().
 */

import type { TemporalState } from "zundo";
import { temporal } from "zundo";
import { create, useStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import type { BuildProgress } from "../domain/build/getProjectBuildGuide";
import type { FinishConfig } from "../domain/finishes/types";
import type { SiteConditions } from "../domain/foundation/siteConditions";
import type { WallOpening } from "../domain/framing/generateFramedWall";
import type { InteriorLayout } from "../domain/interior/types";
import {
	type ClimateProfile,
	type LocationMode,
	type Market,
} from "../domain/location/types";
import type { SaunaRotationDeg } from "../domain/viewer/saunaRotation";
import type { TimeOfDayValue } from "../domain/viewer/timeOfDay";
import type {
	DeckConfig,
	RoofConfig,
	SaunaDimensions,
	ThermalInputs,
	TwoRoomConfig,
	VisibilityFlags,
} from "../engines/types";
import type { Design, SaunaType } from "../types/design";
import {
	BUILDER_HISTORY_LIMIT,
	INITIAL_BUILDER_STATE,
	type PartializedBuilderState,
	partializeBuilderState,
} from "./builderStoreConfig";
import { createDraftActions } from "./builderStoreDraftActions";
import {
	type AutoSaveConfig,
	createPersistenceActions,
	loadDesign,
	startAutoSave,
	stopAutoSave,
} from "./builderStorePersistence";
import type { TwoRoomPresetId } from "./twoRoomPresets";

// ─── State Shape ────────────────────────────────────────────

export interface BuilderState {
	// Draft state (what the user is editing)
	market: Market;
	locationMode: LocationMode;
	climateProfile: ClimateProfile;
	dimensions: SaunaDimensions;
	partitionSplit: number;
		twoRoomConfig: TwoRoomConfig;
		timeOfDay: TimeOfDayValue;
		/**
		 * Whole-building orientation on the plot, in degrees about the vertical
		 * axis. Always one of 0/90/180/270 — see `domain/viewer/saunaRotation`.
		 *
		 * It is not a `BuildInput` field, but it is NOT inert either. On a
		 * **flat** plot it is pure placement: the viewer applies it as one
		 * transform on the sauna rig, nothing dirties, no BOM row moves. On a
		 * **sloping** plot it reaches the engine through the site conditions
		 * (`rotateSlopeToModelFrame`, applied in `useBuilderDerivedData`),
		 * because which plinths carry the drop genuinely depends on which way
		 * the building faces — so it changes plinth heights and the
		 * recommended plinth length.
		 *
		 * Anything that runs `calculateMaterials` from a SAVED design owes the
		 * same conversion, or the same design quotes two different answers.
		 * Current call sites: `useBuilderDerivedData`, `SharePage`,
		 * `buildSharedDesignSummary`, `createComparisonCandidates`.
		 */
		rotationDeg: SaunaRotationDeg;
		/**
		 * The wizard's "2/4/6 personer" size-tier choice, or `null` if none
		 * applies (a template, a design never seeded from the wizard, or one
		 * resized away from the tier since — `setDimensions` clears it). Caps
		 * the 3D viewer's scale-figure estimate; never reaches BOM/BBR/pricing.
		 */
		wizardCapacityHint: number | null;
		visibility: VisibilityFlags;
	openings: WallOpening[];
	deckConfig: DeckConfig | null;
	roofConfig: RoofConfig;
	buildProgress: BuildProgress;
	thermalInputs: ThermalInputs;
	siteConditions: SiteConditions;
	/**
	 * User-authored interior layout (lavar + aggregat), or `null` for "not
	 * authored yet" — in which case the engines generate the default two-tier
	 * layout. Keeping `null` distinct from "the default, materialised" is what
	 * lets every design saved before issue 130 load unchanged, and is also the
	 * only honest record of what the USER decided.
	 *
	 * The stored value is authored intent. Reconciliation against the current
	 * room (trimming runs outside new walls, moving the heater to the nearest
	 * position that fits) happens in `resolveInteriorLayout` on every
	 * derivation, so nothing downstream ever sees a corrupt intermediate.
	 */
	interiorLayout: InteriorLayout | null;
	/**
	 * Karaktärsval — interior wood, panel direction, exterior colour (issue
	 * 134). Unlike `interiorLayout` this is NOT nullable: the three choices
	 * always have a value, the defaults are exactly today's look, and a swatch
	 * row with nothing selected would be a worse lie than a default.
	 */
	finishes: FinishConfig;
	/**
	 * Chosen wall height in metres (issue 155). `null` = never chosen, which
	 * builds the rule default (2.1 m) — the state of every design saved before
	 * the field existed. Read it through `resolveWallHeightM`.
	 */
	wallHeightM: number | null;
	/**
	 * Inomhus only (issues 174/175): which sauna walls stand against the room's
	 * walls, and how much house is above the sauna ceiling. `null` = never
	 * edited, which builds `DEFAULT_INDOOR_ROOM`. Read it through
	 * `normalizeIndoorRoom`.
	 */
	indoorRoom: IndoorRoomConfig | null;
	projectName: string;
	postalCode: string;

	// Persistence state
	saving: boolean;
	saveStatus: string | null;
	loadingDesign: boolean;
}

export interface BuilderActions {
	setDimensions: (d: SaunaDimensions) => void;
	setPartitionSplit: (split: number) => void;
	setTwoRoomConfig: (config: Partial<TwoRoomConfig>) => void;
		setTwoRoomPreset: (preset: TwoRoomPresetId) => void;
		setTimeOfDay: (value: TimeOfDayValue) => void;
		/** Commit an orientation. Any angle is accepted; the stored value is snapped. */
		setRotationDeg: (deg: number) => void;
		/** Turn a quarter step; positive = clockwise seen from above. */
		stepRotation: (steps: number) => void;
		setWizardCapacityHint: (hint: number | null) => void;
		setVisibility: (v: VisibilityFlags) => void;
	setOpenings: (
		v: WallOpening[] | ((prev: WallOpening[]) => WallOpening[]),
	) => void;
	setDeckConfig: (config: DeckConfig | null) => void;
	setRoofConfig: (config: RoofConfig) => void;
	setBuildProgress: (
		v: BuildProgress | ((prev: BuildProgress) => BuildProgress),
	) => void;
	setThermalInputs: (t: ThermalInputs) => void;
	setSiteConditions: (conditions: SiteConditions) => void;
	/** Commit an interior layout. `null` hands the room back to the generator. */
	setInteriorLayout: (layout: InteriorLayout | null) => void;
	/** Commit one or more karaktärsval; unspecified fields keep their value. */
	setFinishes: (finishes: Partial<FinishConfig>) => void;
	/** Commit a wall height; snapped to 5 cm and clamped to the range. */
	setWallHeightM: (wallHeightM: number) => void;
	/** Commit the indoor room; normalized (canonical wall order, clamped height). */
	setIndoorRoom: (indoorRoom: IndoorRoomConfig) => void;
	setProjectName: (name: string) => void;
	setLocationMode: (mode: LocationMode) => void;
	setClimateProfile: (profile: ClimateProfile) => void;
	setPostalCode: (code: string) => void;

	/** Populate store from a saved design row */
	hydrateFromDesign: (design: Design) => void;

	/** Reset to engine defaults (used when no designId or when type changes) */
	resetToDefaults: (input: {
		type: SaunaType;
		dimensions: SaunaDimensions;
		partitionSplit?: number;
			twoRoomConfig?: Partial<TwoRoomConfig>;
			timeOfDay?: TimeOfDayValue;
			rotationDeg?: number;
			wizardCapacityHint?: number | null;
			market: Market;
		thermalInputs?: Partial<ThermalInputs>;
		siteConditions?: Partial<SiteConditions>;
		roofConfig?: Partial<RoofConfig>;
		locationMode?: LocationMode;
		postalCode?: string;
		climateProfile?: Partial<ClimateProfile>;
	}) => void;

	/** Apply a pre-curated builder template on top of the current market context. */
	loadTemplate: (input: {
		type: SaunaType;
		dimensions: SaunaDimensions;
		partitionSplit?: number;
			twoRoomConfig?: Partial<TwoRoomConfig>;
			timeOfDay?: TimeOfDayValue;
			rotationDeg?: number;
			openings: WallOpening[];
		deckConfig: DeckConfig | null;
		roofConfig: RoofConfig;
		thermalInputs?: Partial<ThermalInputs>;
	}) => void;

	/** Explicit save — prompts login if unauthenticated */
	saveNow: (opts: {
		userId: string | null;
		designId: string | null;
		type: SaunaType;
		setSearchParams: (
			nextInit: URLSearchParams | Record<string, string>,
			opts?: { replace?: boolean },
		) => void;
	}) => Promise<boolean>;

	/** Internal: clear saveStatus after a delay */
	_scheduleSaveStatusReset: (delayMs: number) => void;

	/** Internal: set saving / saveStatus */
	_setSaving: (saving: boolean) => void;
	_setSaveStatus: (status: string | null) => void;
	_setLoadingDesign: (loading: boolean) => void;
}

export type BuilderStore = BuilderState & BuilderActions;

// ─── Store ──────────────────────────────────────────────────

export const useBuilderStore = create<BuilderStore>()(
	subscribeWithSelector(
		temporal(
			(set, get): BuilderStore => ({
				...INITIAL_BUILDER_STATE,
				...createDraftActions(set),
				...createPersistenceActions(set, get),
			}),
			{
				limit: BUILDER_HISTORY_LIMIT,
				partialize: partializeBuilderState,
				// Zundo's undo/redo take an optional numeric `steps`. If a caller
				// leaks a non-number (classically onClick={undo}, where React
				// passes the MouseEvent), splice(-NaN, NaN) yields [] and zundo
				// calls setState(undefined) — zustand v5 treats non-objects as a
				// full replace, wiping the store and crashing every subscriber.
				// Sanitize `steps` so no call site can wipe the store.
				wrapTemporal: (storeInitializer) => (set, get, store) => {
					const state = storeInitializer(set, get, store);
					const sanitizeSteps = (steps?: unknown): number =>
						typeof steps === "number" && Number.isFinite(steps) && steps >= 1
							? Math.floor(steps)
							: 1;
					return {
						...state,
						undo: (steps?: number) => state.undo(sanitizeSteps(steps)),
						redo: (steps?: number) => state.redo(sanitizeSteps(steps)),
					};
				},
			},
		),
	),
);

/** Reactive hook for accessing temporal undo/redo state. */
export const useTemporalStore = <T>(
	selector: (state: TemporalState<PartializedBuilderState>) => T,
) => useStore(useBuilderStore.temporal, selector);

export type { AutoSaveConfig, PartializedBuilderState };
export { loadDesign, startAutoSave, stopAutoSave };
